import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Rocket, Compass, Brain, Zap, Trophy, Map, Sparkles, ArrowRight,
  GraduationCap, ShieldCheck, MessageCircle, Star,
  Cpu, Palette, FlaskConical, TrendingUp, Scale, Leaf
} from 'lucide-react';

const FloatingCard = ({ icon: Icon, title, sub, className, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.6, ease: 'easeOut' }}
    className={`card-soft p-4 sm:p-5 flex items-center gap-3 ${className || ''}`}
  >
    <div className="w-10 h-10 rounded-xl bg-[#0055FF]/10 text-[#0055FF] flex items-center justify-center">
      <Icon strokeWidth={2.5} />
    </div>
    <div>
      <div className="font-display font-extrabold text-ink text-sm sm:text-base">{title}</div>
      <div className="text-xs text-muted-ink">{sub}</div>
    </div>
  </motion.div>
);

export default function Landing() {
  React.useEffect(() => {
    import('@/lib/api').then(({ track }) => track('landing_viewed'));
  }, []);

  return (
    <div className="min-h-screen gradient-bg grain">
      {/* Nav */}
      <nav className="sticky top-0 z-30 glass">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-3 flex items-center justify-center">
          <a href="https://futureready.school/" target="_blank" rel="noopener noreferrer" data-testid="nav-brand-logo-link" className="inline-flex items-center">
            <img src="https://customer-assets-gfyr7b9c.emergentagent.net/wingman/ff1ac768-2fd7-43bf-8ad4-7e8fb9fb1144/attachments/90bfb53e790f41f495c6af59502ad198_Future%20Ready%20%282000%20x%201000%20px%29%20%282000%20x%20800%20px%29%20%282200%20x%201000%20px%29%20%281100%20x%20220%20px%29%20%283%29.png" alt="Future Ready School" className="h-10 sm:h-12 w-auto max-w-[240px] sm:max-w-[280px] object-contain" data-testid="brand-logo" />
          </a>
        </div>
      </nav>

      {/* Hero */}
      <header className="max-w-6xl mx-auto px-5 sm:px-8 pt-10 sm:pt-16 pb-8 sm:pb-12">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="chip mb-5"
            >
              <Sparkles className="w-3.5 h-3.5 text-[#FFD600]" strokeWidth={3} />
              <span>The Career Clarity Test</span>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
              className="font-display font-semibold text-ink text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tighter"
            >
              Find your <span className="text-[#0070fb]">career fit</span>. In just 7 minutes.
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.15 }}
              className="text-body text-base sm:text-lg mt-5 max-w-xl"
            >
              Take this 7-minute Career Fit Assessment to discover your strengths, top career zones, future skill stack, and the kind of projects you should start building.
            </motion.p>
            <p className="text-xs uppercase tracking-[0.2em] text-muted-ink mt-3 font-bold">For students in Grades 8–12</p>

            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Link to="/setup" data-testid="start-quest-cta" className="btn-primary inline-flex items-center justify-center gap-2 group">
                Start My Assessment
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" strokeWidth={3} />
              </Link>
              <a href="#how" data-testid="how-it-works-cta" className="btn-secondary inline-flex items-center justify-center gap-2">
                How it works
              </a>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-muted-ink">
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#00D95F]" strokeWidth={2.5} /> Parent-trusted</div>
              <div className="flex items-center gap-2"><GraduationCap className="w-4 h-4 text-[#0055FF]" strokeWidth={2.5} /> Grade 8–12</div>
              <div className="flex items-center gap-2"><Star className="w-4 h-4 text-[#FFD600]" strokeWidth={2.5} /> 24 questions</div>
            </div>
          </div>

          {/* Visual */}
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }}
              className="relative h-[440px] sm:h-[520px]"
            >
              {/* Outer gradient frame */}
              <div className="absolute inset-0 rounded-[2rem] bg-gradient-to-br from-[#0055FF] via-[#9D4CDD] to-[#FF5A00] p-[2px] shadow-2xl shadow-[#0055FF]/15">
                <div className="w-full h-full rounded-[1.9rem] relative overflow-hidden"
                     style={{ background: 'radial-gradient(120% 80% at 30% 20%, rgba(255,214,0,0.20), transparent 55%), radial-gradient(120% 80% at 80% 90%, rgba(0,217,95,0.16), transparent 55%), #ffffff' }}>

                  {/* Soft 3D abstract blobs */}
                  <motion.div
                    animate={{ y: [0, -10, 0] }} transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                    className="absolute -top-12 -left-8 w-44 h-44 rounded-full opacity-40"
                    style={{ background: 'radial-gradient(circle at 30% 30%, #FFD600 0%, transparent 70%)' }}
                  />
                  <motion.div
                    animate={{ y: [0, 12, 0] }} transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
                    className="absolute -bottom-12 -right-8 w-48 h-48 rounded-full opacity-40"
                    style={{ background: 'radial-gradient(circle at 70% 70%, #9D4CDD 0%, transparent 70%)' }}
                  />

                  {/* 6 Career icon tiles in a 3×2 floating layout */}
                  {[
                    { icon: Cpu,          color: '#0055FF', from: '#66B0FF', to: '#0044CC', top: '6%',  left: '10%',  right: null,  size: 84,  iconSize: 'w-10 h-10', rotate: -7, delay: 0.10 },
                    { icon: Rocket,       color: '#FF5A00', from: '#FFB07A', to: '#D14400', top: '4%',  left: null,   right: '12%', size: 96,  iconSize: 'w-11 h-11', rotate: 6,  delay: 0.18 },
                    { icon: Palette,      color: '#9D4CDD', from: '#C77DFF', to: '#7B2CBF', top: '38%', left: '4%',   right: null,  size: 96,  iconSize: 'w-11 h-11', rotate: 9,  delay: 0.26 },
                    { icon: Brain,        color: '#00A346', from: '#6EF59A', to: '#00A346', top: '36%', left: null,   right: '4%',  size: 84,  iconSize: 'w-10 h-10', rotate: -8, delay: 0.34 },
                    { icon: Scale,        color: '#E0A800', from: '#FFE066', to: '#E0A800', top: '70%', left: '14%',  right: null,  size: 96,  iconSize: 'w-11 h-11', rotate: -6, delay: 0.42 },
                    { icon: Leaf,         color: '#16A34A', from: '#86EFAC', to: '#15803D', top: '72%', left: null,   right: '14%', size: 84,  iconSize: 'w-10 h-10', rotate: 7,  delay: 0.50 },
                  ].map((t, i) => {
                    const Icon = t.icon;
                    return (
                      <div
                        key={i}
                        className="absolute rounded-3xl flex items-center justify-center"
                        style={{
                          top: t.top,
                          left: t.left || undefined,
                          right: t.right || undefined,
                          width: t.size,
                          height: t.size,
                          background: `linear-gradient(145deg, ${t.from} 0%, ${t.to} 100%)`,
                          boxShadow: `0 16px 28px ${t.color}55`,
                          transform: `rotate(${t.rotate}deg)`,
                        }}
                      >
                        <Icon className={`${t.iconSize} text-white drop-shadow-[0_4px_8px_rgba(0,0,0,0.25)]`} strokeWidth={2.2} />
                      </div>
                    );
                  })}

                  {/* Center "Career Fit" wordmark */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <motion.div
                      initial={{ opacity: 0, scale: 0.85 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.6, delay: 0.3, ease: 'easeOut' }}
                      className="text-center"
                    >
                      <div className="font-display font-black text-ink text-5xl sm:text-6xl tracking-tighter leading-none">
                        Career
                      </div>
                      <div className="font-display font-black text-5xl sm:text-6xl tracking-tighter leading-none mt-1.5"
                           style={{ background: 'linear-gradient(90deg, #0055FF, #9D4CDD, #FF5A00)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                        Fit
                      </div>
                    </motion.div>
                  </div>

                  {/* Tiny floating accent stars between tiles */}
                  <motion.div animate={{ y: [0, -6, 0] }} transition={{ duration: 4, repeat: Infinity, delay: 0.2 }}
                    className="absolute" style={{ top: '22%', left: '30%' }}>
                    <Star className="w-5 h-5 text-[#FFD600]" fill="#FFD600" strokeWidth={0} />
                  </motion.div>
                  <motion.div animate={{ y: [0, -7, 0] }} transition={{ duration: 5, repeat: Infinity, delay: 0.6 }}
                    className="absolute" style={{ top: '22%', left: '64%' }}>
                    <Sparkles className="w-5 h-5 text-[#0055FF]" strokeWidth={2.5} />
                  </motion.div>
                  <motion.div animate={{ y: [0, -5, 0] }} transition={{ duration: 4.5, repeat: Infinity, delay: 1 }}
                    className="absolute" style={{ top: '82%', left: '32%' }}>
                    <Star className="w-5 h-5 text-[#FF5A00]" fill="#FF5A00" strokeWidth={0} />
                  </motion.div>
                  <motion.div animate={{ y: [0, -6, 0] }} transition={{ duration: 5, repeat: Infinity, delay: 1.4 }}
                    className="absolute" style={{ top: '82%', left: '62%' }}>
                    <Sparkles className="w-5 h-5 text-[#9D4CDD]" strokeWidth={2.5} />
                  </motion.div>

                  {/* Bottom soft fade */}
                  <div className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-white/40 to-transparent pointer-events-none" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </header>

      {/* Sections */}
      <section id="how" className="max-w-6xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        <div className="grid md:grid-cols-2 gap-6 items-start">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#0055FF]">Not a boring test</p>
            <h2 className="font-display font-black text-ink text-3xl sm:text-4xl mt-2 tracking-tight">This is not a school exam.</h2>
            <p className="text-body mt-4 text-base">
              The Career Fit Assessment is a fun, interactive quiz that helps students understand what they are naturally good at, what career zones they should explore, and what skills they should start building in the AI age.
            </p>
          </div>
          <div className="card-dark p-6 sm:p-8">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#FFD600]">
              <Sparkles className="w-3.5 h-3.5" strokeWidth={3} /> Why students love it
            </div>
            <ul className="mt-4 space-y-3 text-sm">
              <li className="flex gap-3"><Zap className="w-5 h-5 text-[#FFD600] shrink-0" strokeWidth={2.5} /> Smooth, game-style flow — not an exam</li>
              <li className="flex gap-3"><Map className="w-5 h-5 text-[#00D95F] shrink-0" strokeWidth={2.5} /> Personal 1-year exploration roadmap</li>
              <li className="flex gap-3"><Rocket className="w-5 h-5 text-[#FF5A00] shrink-0" strokeWidth={2.5} /> A real mini project idea to start building</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        <h2 className="font-display font-black text-ink text-3xl sm:text-4xl tracking-tight">In 7 minutes, you’ll discover:</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
          {[
            { i: Trophy, t: 'Your Career Avatar', s: 'A game-style identity tied to your strengths', c: '#FFD600' },
            { i: Compass, t: 'Your Top 3 Career Zones', s: 'Primary, secondary and exploration', c: '#0055FF' },
            { i: Brain, t: 'Your Strength Profile', s: 'Top 4 traits that define how you work', c: '#9D4CDD' },
            { i: Zap, t: 'Your Future Skill Stack', s: '5 skills to start building now', c: '#00D95F' },
            { i: Rocket, t: 'Your Mini Project Idea', s: 'A real thing you can build this month', c: '#FF5A00' },
            { i: Map, t: 'Your 1-Year Roadmap', s: '30 days, 3, 6 and 12 month plan', c: '#0F172A' },
          ].map((it, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.45, delay: idx * 0.05 }}
              className="card-soft p-5 sm:p-6 hover:-translate-y-1 transition-transform"
            >
              <div className="w-11 h-11 rounded-2xl flex items-center justify-center mb-4" style={{ background: `${it.c}1A`, color: it.c }}>
                <it.i strokeWidth={2.5} />
              </div>
              <div className="font-display font-extrabold text-ink text-lg">{it.t}</div>
              <div className="text-sm text-muted-ink mt-1">{it.s}</div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        <h2 className="font-display font-black text-ink text-3xl sm:text-4xl tracking-tight">Perfect for students who are thinking:</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-8">
          {[
            'I don’t know what I want to do.',
            'I have too many interests.',
            'I want to know what I’m good at.',
            'I want to explore future careers.',
            'I want to build something cool.',
            'I need a starting point.',
          ].map((q, i) => (
            <div key={i} className="card-soft p-5 flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-[#FFD600]/20 text-[#0F172A] flex items-center justify-center font-display font-black text-sm">{i + 1}</div>
              <div className="font-display font-bold text-ink">“{q}”</div>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-14 text-center">
        <h2 className="font-display font-black text-ink text-3xl sm:text-5xl tracking-tighter">Ready to play?</h2>
        <p className="text-body mt-3">Your Future Profile is just 7 minutes away.</p>
        <div className="mt-7">
          <Link to="/setup" data-testid="start-quest-bottom-cta" className="btn-primary inline-flex items-center justify-center gap-2">
            Start My Assessment <ArrowRight className="w-4 h-4" strokeWidth={3} />
          </Link>
        </div>
        <p className="text-xs text-muted-ink mt-8">© Future Ready School · The Career Fit Assessment is a starting point, not a final career decision.</p>
      </section>
    </div>
  );
}

